/**
 * A small, safe formula language for user-defined calculated columns.
 *
 * Deliberately NOT JavaScript: no `eval`, no `Function`, no property access,
 * no loops. Just arithmetic, comparisons, logic, a curated function library,
 * and `[Field Name]` references that resolve to the row's column values. This
 * gives a lot of spreadsheet-style flexibility while staying sandboxed.
 *
 * Pipeline: tokenize -> recursive-descent parse (precedence climbing) -> AST
 * -> evaluate against a per-row resolver. Compile once, evaluate per row.
 */

export type FormulaValue = number | string | boolean | null;

/** Resolves a `[Field]` reference to a value for the current row. */
export type FieldResolver = (name: string) => FormulaValue;

export interface CompiledFormula {
  ok: boolean;
  /** Human-readable syntax/validation error when `ok` is false. */
  error?: string;
  /** Field names ([refs]) the expression touches — handy for diagnostics. */
  fields: string[];
  evaluate: (resolve: FieldResolver) => FormulaValue;
}

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type Tok =
  | { t: 'num'; v: number }
  | { t: 'str'; v: string }
  | { t: 'col'; v: string }
  | { t: 'ident'; v: string }
  | { t: 'op'; v: string };

const TWO_CHAR_OPS = ['==', '!=', '<>', '<=', '>=', '&&', '||'];
const ONE_CHAR_OPS = '+-*/%^&<>=(),!';

function tokenize(src: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  const isDigit = (c: string) => c >= '0' && c <= '9';
  const isIdentStart = (c: string) => /[A-Za-z_]/.test(c);
  const isIdent = (c: string) => /[A-Za-z0-9_]/.test(c);

  while (i < src.length) {
    const c = src[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      i++;
      continue;
    }
    // [Field Name] reference
    if (c === '[') {
      const close = src.indexOf(']', i + 1);
      if (close < 0) throw new Error('Unclosed "[" in a field reference.');
      toks.push({ t: 'col', v: src.slice(i + 1, close).trim() });
      i = close + 1;
      continue;
    }
    // string literal
    if (c === '"' || c === "'") {
      const quote = c;
      let j = i + 1;
      let s = '';
      while (j < src.length && src[j] !== quote) {
        if (src[j] === '\\' && j + 1 < src.length) {
          s += src[j + 1];
          j += 2;
        } else {
          s += src[j];
          j++;
        }
      }
      if (j >= src.length) throw new Error('Unclosed string literal.');
      toks.push({ t: 'str', v: s });
      i = j + 1;
      continue;
    }
    // number
    if (isDigit(c) || (c === '.' && isDigit(src[i + 1]))) {
      let j = i;
      while (j < src.length && /[0-9.]/.test(src[j])) j++;
      toks.push({ t: 'num', v: parseFloat(src.slice(i, j)) });
      i = j;
      continue;
    }
    // identifier (function name / keyword)
    if (isIdentStart(c)) {
      let j = i;
      while (j < src.length && isIdent(src[j])) j++;
      toks.push({ t: 'ident', v: src.slice(i, j) });
      i = j;
      continue;
    }
    // operators
    const two = src.slice(i, i + 2);
    if (TWO_CHAR_OPS.includes(two)) {
      toks.push({ t: 'op', v: two });
      i += 2;
      continue;
    }
    if (ONE_CHAR_OPS.includes(c)) {
      toks.push({ t: 'op', v: c });
      i++;
      continue;
    }
    throw new Error(`Unexpected character "${c}".`);
  }
  return toks;
}

// ---------------------------------------------------------------------------
// Parser (AST)
// ---------------------------------------------------------------------------

type Node =
  | { k: 'num'; v: number }
  | { k: 'str'; v: string }
  | { k: 'bool'; v: boolean }
  | { k: 'null' }
  | { k: 'col'; name: string }
  | { k: 'unary'; op: string; x: Node }
  | { k: 'bin'; op: string; a: Node; b: Node }
  | { k: 'call'; name: string; args: Node[] };

// Binary operator precedence. Higher binds tighter. `^` is right-associative.
const BIN_PREC: Record<string, number> = {
  '||': 1, OR: 1,
  '&&': 2, AND: 2,
  '=': 3, '==': 3, '!=': 3, '<>': 3, '<': 3, '>': 3, '<=': 3, '>=': 3,
  '+': 4, '-': 4, '&': 4,
  '*': 5, '/': 5, '%': 5,
  '^': 6,
};

class Parser {
  private pos = 0;
  constructor(private toks: Tok[]) {}

  private peek(): Tok | undefined {
    return this.toks[this.pos];
  }
  private next(): Tok | undefined {
    return this.toks[this.pos++];
  }

  /** Normalized infix operator name for the current token, or null. */
  private infixOp(): string | null {
    const t = this.peek();
    if (!t) return null;
    if (t.t === 'op' && t.v in BIN_PREC) return t.v;
    if (t.t === 'ident') {
      const u = t.v.toUpperCase();
      if (u === 'AND' || u === 'OR') return u;
    }
    return null;
  }

  parse(): Node {
    const node = this.parseExpr(0);
    if (this.pos < this.toks.length) {
      const t = this.peek()!;
      throw new Error(`Unexpected "${'v' in t ? t.v : ''}".`);
    }
    return node;
  }

  private parseExpr(minPrec: number): Node {
    let left = this.parseUnary();
    for (;;) {
      const op = this.infixOp();
      if (op == null) break;
      const prec = BIN_PREC[op];
      if (prec < minPrec) break;
      this.next(); // consume operator
      const nextMin = op === '^' ? prec : prec + 1; // ^ is right-assoc
      const right = this.parseExpr(nextMin);
      left = { k: 'bin', op, a: left, b: right };
    }
    return left;
  }

  private parseUnary(): Node {
    const t = this.peek();
    if (t?.t === 'op' && t.v === '-') {
      this.next();
      return { k: 'unary', op: '-', x: this.parseUnary() };
    }
    if (t?.t === 'op' && t.v === '+') {
      this.next();
      return this.parseUnary();
    }
    if ((t?.t === 'op' && t.v === '!') || (t?.t === 'ident' && t.v.toUpperCase() === 'NOT')) {
      this.next();
      return { k: 'unary', op: '!', x: this.parseUnary() };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): Node {
    const t = this.next();
    if (!t) throw new Error('Unexpected end of formula.');
    if (t.t === 'num') return { k: 'num', v: t.v };
    if (t.t === 'str') return { k: 'str', v: t.v };
    if (t.t === 'col') {
      if (!t.v) throw new Error('Empty field reference "[]".');
      return { k: 'col', name: t.v };
    }
    if (t.t === 'op' && t.v === '(') {
      const e = this.parseExpr(0);
      const close = this.next();
      if (!close || close.t !== 'op' || close.v !== ')') throw new Error('Missing ")".');
      return e;
    }
    if (t.t === 'ident') {
      const u = t.v.toUpperCase();
      if (u === 'TRUE') return { k: 'bool', v: true };
      if (u === 'FALSE') return { k: 'bool', v: false };
      if (u === 'NULL') return { k: 'null' };
      if (u === 'PI') return { k: 'num', v: Math.PI };
      // function call: must be followed by "("
      const nxt = this.peek();
      if (nxt?.t === 'op' && nxt.v === '(') {
        this.next(); // consume "("
        const args: Node[] = [];
        if (!(this.peek()?.t === 'op' && (this.peek() as any).v === ')')) {
          for (;;) {
            args.push(this.parseExpr(0));
            const sep = this.peek();
            if (sep?.t === 'op' && sep.v === ',') {
              this.next();
              continue;
            }
            break;
          }
        }
        const close = this.next();
        if (!close || close.t !== 'op' || close.v !== ')') throw new Error(`Missing ")" after ${u}(.`);
        return { k: 'call', name: u, args };
      }
      throw new Error(`Unknown name "${t.v}". Wrap column names in [brackets], e.g. [${t.v}].`);
    }
    throw new Error(`Unexpected "${'v' in t ? t.v : ''}".`);
  }
}

// ---------------------------------------------------------------------------
// Coercion helpers
// ---------------------------------------------------------------------------

const DAY_MS = 86400000;

export function coerceNumber(v: FormulaValue): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v == null) return NaN;
  const s = String(v).trim().replace(/[$,]/g, '');
  if (s === '') return NaN;
  return Number(s);
}

function coerceStr(v: FormulaValue): string {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

function coerceBool(v: FormulaValue): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== '0';
}

/** Interpret a value as a date, returning epoch ms (NaN if unparseable). */
function coerceDateMs(v: FormulaValue): number {
  if (typeof v === 'number') return v; // already epoch ms (date columns resolve to their timestamp)
  if (v == null) return NaN;
  const s = String(v).trim();
  if (s === '') return NaN;
  return Date.parse(s);
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function daysBetween(a: FormulaValue, b: FormulaValue): number {
  const am = startOfDay(coerceDateMs(a));
  const bm = startOfDay(coerceDateMs(b));
  if (isNaN(am) || isNaN(bm)) return NaN;
  return Math.round((bm - am) / DAY_MS);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatDate(ms: number, fmt?: string): string {
  if (isNaN(ms)) return '';
  const d = new Date(ms);
  if (!fmt) return d.toLocaleDateString();
  return fmt
    .replace(/YYYY/g, String(d.getFullYear()))
    .replace(/MM/g, pad2(d.getMonth() + 1))
    .replace(/DD/g, pad2(d.getDate()));
}

function looseEq(a: FormulaValue, b: FormulaValue): boolean {
  if (typeof a === 'number' || typeof b === 'number') {
    const an = coerceNumber(a);
    const bn = coerceNumber(b);
    if (!isNaN(an) && !isNaN(bn)) return an === bn;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') return coerceBool(a) === coerceBool(b);
  return coerceStr(a) === coerceStr(b);
}

function compare(a: FormulaValue, b: FormulaValue): number {
  const an = coerceNumber(a);
  const bn = coerceNumber(b);
  if (!isNaN(an) && !isNaN(bn)) return an < bn ? -1 : an > bn ? 1 : 0;
  const as = coerceStr(a);
  const bs = coerceStr(b);
  return as < bs ? -1 : as > bs ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Function library
// ---------------------------------------------------------------------------

type Fn = (args: FormulaValue[]) => FormulaValue;

const FUNCS: Record<string, Fn> = {
  // Math
  ABS: (a) => Math.abs(coerceNumber(a[0])),
  ROUND: (a) => {
    const d = a.length > 1 ? coerceNumber(a[1]) : 0;
    const f = Math.pow(10, d);
    return Math.round(coerceNumber(a[0]) * f) / f;
  },
  FLOOR: (a) => Math.floor(coerceNumber(a[0])),
  CEIL: (a) => Math.ceil(coerceNumber(a[0])),
  SQRT: (a) => Math.sqrt(coerceNumber(a[0])),
  POW: (a) => Math.pow(coerceNumber(a[0]), coerceNumber(a[1])),
  MOD: (a) => coerceNumber(a[0]) % coerceNumber(a[1]),
  MIN: (a) => Math.min(...a.map(coerceNumber)),
  MAX: (a) => Math.max(...a.map(coerceNumber)),
  SUM: (a) => a.reduce<number>((s, x) => s + coerceNumber(x), 0),
  AVG: (a) => (a.length ? a.reduce<number>((s, x) => s + coerceNumber(x), 0) / a.length : NaN),

  // Logic
  IF: (a) => (coerceBool(a[0]) ? a[1] ?? null : a[2] ?? null),
  AND: (a) => a.every(coerceBool),
  OR: (a) => a.some(coerceBool),
  NOT: (a) => !coerceBool(a[0]),
  ISBLANK: (a) => a[0] == null || coerceStr(a[0]).trim() === '',
  COALESCE: (a) => {
    for (const x of a) if (x != null && coerceStr(x).trim() !== '') return x;
    return null;
  },

  // Text
  CONCAT: (a) => a.map(coerceStr).join(''),
  UPPER: (a) => coerceStr(a[0]).toUpperCase(),
  LOWER: (a) => coerceStr(a[0]).toLowerCase(),
  TRIM: (a) => coerceStr(a[0]).trim(),
  LEN: (a) => coerceStr(a[0]).length,
  LEFT: (a) => coerceStr(a[0]).slice(0, coerceNumber(a[1])),
  RIGHT: (a) => {
    const n = coerceNumber(a[1]);
    return n <= 0 ? '' : coerceStr(a[0]).slice(-n);
  },
  MID: (a) => coerceStr(a[0]).substr(coerceNumber(a[1]), coerceNumber(a[2])),
  CONTAINS: (a) => coerceStr(a[0]).toLowerCase().includes(coerceStr(a[1]).toLowerCase()),
  REPLACE: (a) => coerceStr(a[0]).split(coerceStr(a[1])).join(coerceStr(a[2])),

  // Date
  TODAY: () => startOfDay(Date.now()),
  NOW: () => Date.now(),
  DATE: (a) => Date.parse(coerceStr(a[0])),
  DAYS_AGO: (a) => daysBetween(a[0], startOfDay(Date.now())),
  DAYS_FROM_NOW: (a) => daysBetween(startOfDay(Date.now()), a[0]),
  DAYS_BETWEEN: (a) => daysBetween(a[0], a[1]),
  DATEADD: (a) => coerceDateMs(a[0]) + coerceNumber(a[1]) * DAY_MS,
  YEAR: (a) => new Date(coerceDateMs(a[0])).getFullYear(),
  MONTH: (a) => new Date(coerceDateMs(a[0])).getMonth() + 1,
  DAY: (a) => new Date(coerceDateMs(a[0])).getDate(),
  WEEKDAY: (a) => new Date(coerceDateMs(a[0])).getDay(), // 0 = Sunday
  FORMATDATE: (a) => formatDate(coerceDateMs(a[0]), a[1] != null ? coerceStr(a[1]) : undefined),
};

/** Function names + arity hints for help text / autocomplete. */
export const FUNCTION_NAMES = Object.keys(FUNCS);

// ---------------------------------------------------------------------------
// Evaluator
// ---------------------------------------------------------------------------

function evalNode(node: Node, resolve: FieldResolver): FormulaValue {
  switch (node.k) {
    case 'num':
      return node.v;
    case 'str':
      return node.v;
    case 'bool':
      return node.v;
    case 'null':
      return null;
    case 'col':
      return resolve(node.name);
    case 'unary': {
      const x = evalNode(node.x, resolve);
      return node.op === '-' ? -coerceNumber(x) : !coerceBool(x);
    }
    case 'bin': {
      const { op } = node;
      // Short-circuit logical ops.
      if (op === '||' || op === 'OR') return coerceBool(evalNode(node.a, resolve)) || coerceBool(evalNode(node.b, resolve));
      if (op === '&&' || op === 'AND') return coerceBool(evalNode(node.a, resolve)) && coerceBool(evalNode(node.b, resolve));
      const a = evalNode(node.a, resolve);
      const b = evalNode(node.b, resolve);
      switch (op) {
        case '=':
        case '==':
          return looseEq(a, b);
        case '!=':
        case '<>':
          return !looseEq(a, b);
        case '<':
          return compare(a, b) < 0;
        case '>':
          return compare(a, b) > 0;
        case '<=':
          return compare(a, b) <= 0;
        case '>=':
          return compare(a, b) >= 0;
        case '+':
          return coerceNumber(a) + coerceNumber(b);
        case '-':
          return coerceNumber(a) - coerceNumber(b);
        case '*':
          return coerceNumber(a) * coerceNumber(b);
        case '/':
          return coerceNumber(a) / coerceNumber(b);
        case '%':
          return coerceNumber(a) % coerceNumber(b);
        case '^':
          return Math.pow(coerceNumber(a), coerceNumber(b));
        case '&':
          return coerceStr(a) + coerceStr(b);
      }
      throw new Error(`Unknown operator "${op}".`);
    }
    case 'call': {
      const fn = FUNCS[node.name];
      if (!fn) throw new Error(`Unknown function "${node.name}".`);
      return fn(node.args.map((arg) => evalNode(arg, resolve)));
    }
  }
}

/** Walk the AST to collect field refs and validate function names up front. */
function inspect(node: Node, fields: Set<string>): void {
  switch (node.k) {
    case 'col':
      fields.add(node.name);
      return;
    case 'unary':
      inspect(node.x, fields);
      return;
    case 'bin':
      inspect(node.a, fields);
      inspect(node.b, fields);
      return;
    case 'call':
      if (!FUNCS[node.name]) throw new Error(`Unknown function "${node.name}".`);
      node.args.forEach((a) => inspect(a, fields));
      return;
  }
}

/**
 * Parse + validate a formula once. The returned `evaluate` is safe to call per
 * row; runtime errors degrade to `null` rather than throwing into render.
 */
export function compileFormula(expression: string): CompiledFormula {
  const expr = expression.trim();
  if (expr === '') {
    return { ok: false, error: 'Formula is empty.', fields: [], evaluate: () => null };
  }
  let ast: Node;
  const fields = new Set<string>();
  try {
    ast = new Parser(tokenize(expr)).parse();
    inspect(ast, fields);
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Invalid formula.',
      fields: [],
      evaluate: () => null,
    };
  }
  return {
    ok: true,
    fields: [...fields],
    evaluate: (resolve) => {
      try {
        return evalNode(ast, resolve);
      } catch {
        return null;
      }
    },
  };
}
